import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatPrice, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Plane, X, ChevronLeft, ChevronRight, BadgeCheck, ShieldAlert, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function IncomingProductsGallery() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [showReserve, setShowReserve] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [reserveForm, setReserveForm] = useState({ customer_name: "", customer_phone: "", payment_method: "transferencia", notes: "" });

    const load = () => api.get("/incoming-products").then(({ data }) => setItems(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (user && selected && !reserveForm.customer_name) {
            setReserveForm((f) => ({ ...f, customer_name: user.name || "" }));
        }
    }, [user, selected, reserveForm.customer_name]);

    const openItem = (it) => { setSelected(it); setImgIdx(0); setShowReserve(false); setQuantity(1); };
    const close = () => { setSelected(null); setShowReserve(false); setQuantity(1); };
    const nextImg = () => setImgIdx((i) => (i + 1) % (selected?.images?.length || 1));
    const prevImg = () => setImgIdx((i) => (i - 1 + (selected?.images?.length || 1)) % (selected?.images?.length || 1));

    // Lock body scroll while modal is open (prevents background from scrolling on mobile)
    useEffect(() => {
        if (!selected) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [selected]);

    const submitReserve = async (e) => {
        e.preventDefault();
        if (!user) { toast.error("Inicia sesión para apartar"); return; }
        if (!user.verified) { toast.error("Tu cuenta debe estar verificada"); return; }
        try {
            await api.post(`/incoming-products/${selected._id}/reserve`, {
                ...reserveForm,
                incoming_id: selected._id,
                quantity,
            });
            toast.success("¡Apartado registrado! Te contactaremos para coordinar el anticipo.");
            close();
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al apartar");
        }
    };

    const remainingOf = (it) => Math.max(0, (parseInt(it.quantity_available || 0)) - (parseInt(it.quantity_reserved || 0)));
    const depositOf = (it, qty) => {
        const p = parseFloat(it.estimated_price || 0);
        const pct = parseInt(it.deposit_percent || 30);
        return p > 0 ? (p * pct * qty) / 100 : 0;
    };

    const remaining = selected ? remainingOf(selected) : 0;

    return (
        <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[var(--tb-border)]" data-testid="incoming-section">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                    <div className="tb-overline text-[var(--tb-gold)] mb-3 flex items-center gap-2">
                        <Plane className="w-4 h-4" /> En tránsito
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Productos en camino</h2>
                    <p className="text-[var(--tb-text-2)] text-sm mt-2 max-w-2xl">
                        Piezas seleccionadas que vienen en tránsito hacia nuestra boutique. Apártalas con un anticipo y serán tuyas en cuanto lleguen.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.length === 0 ? (
                    <div className="col-span-full tb-card p-10 text-center text-[var(--tb-text-2)]">
                        <Plane className="w-10 h-10 text-[var(--tb-gold)] mx-auto mb-3 opacity-50" />
                        <p className="text-sm">Estamos seleccionando piezas exclusivas. Pronto verás aquí los productos en camino que podrás apartar antes de que lleguen.</p>
                    </div>
                ) : items.map((it) => {
                    const rem = remainingOf(it);
                    return (
                        <button key={it._id} onClick={() => openItem(it)} className="tb-card overflow-hidden text-left hover:border-[var(--tb-gold)] transition group" data-testid={`incoming-${it._id}`}>
                            <div className="relative aspect-square overflow-hidden bg-[var(--tb-bg-2)]">
                                {it.images?.[0] ? <img src={it.images[0]} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition" /> :
                                    <div className="w-full h-full flex items-center justify-center text-[var(--tb-muted)]">Sin imagen</div>}
                                <span className="absolute top-2 left-2 tb-badge tb-badge-gold !text-[10px]">En camino</span>
                                {rem === 0 && <span className="absolute top-2 right-2 tb-badge tb-badge-warning !text-[10px]">Todo apartado</span>}
                            </div>
                            <div className="p-3">
                                {it.sku && <div className="text-[10px] text-[var(--tb-muted)] font-mono">{it.sku}</div>}
                                <div className="font-medium text-sm truncate">{it.name}</div>
                                {parseFloat(it.estimated_price || 0) > 0 && (
                                    <div className="text-[var(--tb-gold)] text-sm mt-1">{formatPrice(it.estimated_price)}</div>
                                )}
                                <div className="text-[10px] text-[var(--tb-text-2)] mt-0.5">
                                    {rem} de {it.quantity_available || 0} disponible(s)
                                </div>
                                {it.estimated_arrival && (
                                    <div className="text-[10px] text-[var(--tb-muted)]">Llega: {it.estimated_arrival}</div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {selected && createPortal(
                <div className="fixed inset-0 bg-black/70 z-[200] overflow-y-auto fade-in" onClick={close} style={{ isolation: "isolate" }}>
                    <button onClick={close} className="fixed top-3 right-3 z-[210] bg-black/70 hover:bg-black/90 rounded-full w-10 h-10 flex items-center justify-center text-white shadow-lg" data-testid="incoming-close" aria-label="Cerrar">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="min-h-full flex items-start md:items-center justify-center p-4">
                        <div className="relative bg-[var(--tb-bg)] border border-[var(--tb-border)] max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="incoming-modal">
                            <div className="grid md:grid-cols-2">
                                <div className="relative aspect-square bg-[var(--tb-bg-2)]">
                                    {selected.images?.[imgIdx] && <img src={selected.images[imgIdx]} alt="" className="w-full h-full object-cover" />}
                                    {(selected.images?.length || 0) > 1 && (
                                        <>
                                            <button onClick={prevImg} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                                            <button onClick={nextImg} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                                        </>
                                    )}
                                    <span className="absolute top-3 left-3 tb-badge tb-badge-gold">En tránsito</span>
                                </div>

                                <div className="p-6 space-y-3">
                                    {selected.sku && <div className="text-xs text-[var(--tb-muted)] font-mono">{selected.sku}</div>}
                                    <h3 className="font-display text-2xl">{selected.name}</h3>
                                    {selected.description && (
                                        <p className="text-sm text-[var(--tb-text-2)] whitespace-pre-wrap">{selected.description}</p>
                                    )}
                                    {parseFloat(selected.estimated_price || 0) > 0 && (
                                        <div className="text-2xl text-[var(--tb-gold)] font-semibold">{formatPrice(selected.estimated_price)} <span className="text-xs text-[var(--tb-muted)]">c/u</span></div>
                                    )}
                                    <div className="text-sm text-[var(--tb-text-2)]">
                                        <span className="text-[var(--tb-muted)]">Disponibles para apartar:</span> {remaining} de {selected.quantity_available || 0}
                                    </div>
                                    {selected.estimated_arrival && (
                                        <div className="text-sm"><span className="text-[var(--tb-muted)]">Llegada estimada:</span> {selected.estimated_arrival}</div>
                                    )}

                                    {/* Gates: not logged / not verified */}
                                    {!user ? (
                                        <Link to="/login?next=/" className="tb-btn-primary w-full text-center block">Acceder para apartar</Link>
                                    ) : !user.verified ? (
                                        <div className="bg-amber-500/10 border border-amber-500/40 p-3 text-sm space-y-2">
                                            <div className="flex items-center gap-2 text-amber-300 font-medium">
                                                <ShieldAlert className="w-4 h-4" /> Cuenta no verificada
                                            </div>
                                            <p className="text-xs text-[var(--tb-text-2)]">Solo las cuentas con check azul <BadgeCheck className="inline w-3 h-3 text-blue-400" /> pueden apartar productos en camino. Verifica tu cuenta desde tu perfil.</p>
                                            <Link to="/perfil?tab=verificacion" className="tb-btn-secondary !text-xs inline-block">Ir a verificar mi cuenta</Link>
                                        </div>
                                    ) : remaining === 0 ? (
                                        <div className="text-sm text-[var(--tb-muted)]">Todas las unidades ya fueron apartadas. ¡Vuelve cuando llegue el siguiente lote!</div>
                                    ) : selected.accepts_reservation === false ? (
                                        <div className="text-xs text-[var(--tb-muted)]">Este producto no acepta apartados. Estará disponible cuando llegue al inventario.</div>
                                    ) : !showReserve ? (
                                        <>
                                            <div className="flex items-center gap-3">
                                                <span className="tb-overline">Cantidad:</span>
                                                <div className="flex items-center border border-[var(--tb-border)]">
                                                    <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-2 py-1 hover:bg-[var(--tb-bg-2)]" data-testid="qty-minus"><Minus className="w-3 h-3" /></button>
                                                    <input type="number" min="1" max={remaining} value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(remaining, parseInt(e.target.value || 1))))} className="w-12 text-center bg-transparent" data-testid="qty-input" />
                                                    <button type="button" onClick={() => setQuantity(Math.min(remaining, quantity + 1))} className="px-2 py-1 hover:bg-[var(--tb-bg-2)]" data-testid="qty-plus"><Plus className="w-3 h-3" /></button>
                                                </div>
                                                <span className="text-xs text-[var(--tb-muted)]">máx {remaining}</span>
                                            </div>
                                            <div className="bg-[var(--tb-border-gold)]/10 border border-[var(--tb-gold)]/30 p-3 text-sm">
                                                Total estimado: <span className="font-bold">{formatPrice((selected.estimated_price || 0) * quantity)}</span><br/>
                                                Apártalo con <span className="font-bold">{selected.deposit_percent || 30}% de anticipo</span>: <span className="text-[var(--tb-gold)] font-bold">{formatPrice(depositOf(selected, quantity))}</span>
                                            </div>
                                            <button onClick={() => setShowReserve(true)} className="tb-btn-primary w-full" data-testid="incoming-reserve-btn">
                                                Apartar {quantity} unidad(es)
                                            </button>
                                        </>
                                    ) : (
                                        <form onSubmit={submitReserve} className="space-y-3 pt-2 border-t border-[var(--tb-border)]" data-testid="incoming-reserve-form">
                                            <div className="tb-overline text-[var(--tb-gold)]">Datos para apartar</div>
                                            <div className="text-xs text-[var(--tb-text-2)]">
                                                Cantidad: <span className="font-semibold">{quantity}</span> · Anticipo: <span className="text-[var(--tb-gold)] font-semibold">{formatPrice(depositOf(selected, quantity))}</span>
                                            </div>
                                            <input className="tb-input" required placeholder="Nombre completo *" value={reserveForm.customer_name} onChange={(e) => setReserveForm({ ...reserveForm, customer_name: e.target.value })} data-testid="incoming-name" />
                                            <input className="tb-input" required placeholder="Teléfono / WhatsApp *" value={reserveForm.customer_phone} onChange={(e) => setReserveForm({ ...reserveForm, customer_phone: e.target.value })} data-testid="incoming-phone" />
                                            <select className="tb-input" value={reserveForm.payment_method} onChange={(e) => setReserveForm({ ...reserveForm, payment_method: e.target.value })} data-testid="incoming-payment">
                                                <option value="transferencia">Transferencia bancaria</option>
                                                <option value="paypal">PayPal</option>
                                            </select>
                                            <textarea className="tb-input min-h-[60px]" placeholder="Notas (talla, color preferido, etc.)" value={reserveForm.notes} onChange={(e) => setReserveForm({ ...reserveForm, notes: e.target.value })} data-testid="incoming-notes" />
                                            <p className="text-[10px] text-[var(--tb-muted)]">El pago es exclusivamente en línea (transferencia o PayPal). Te enviaremos los detalles del pago tras confirmar.</p>
                                            <button type="submit" className="tb-btn-primary w-full" data-testid="incoming-submit">Confirmar apartado</button>
                                            <button type="button" onClick={() => setShowReserve(false)} className="tb-link text-xs">Cancelar</button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </section>
    );
}
